import { useEffect, useState } from 'react';
import { Plus, ShieldAlert, UserMinus, ShieldCheck } from 'lucide-react';
import {
  Card,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  FormField,
  StatusBadge,
  NotificationCard,
  UniversalLoadingState,
  useToast,
} from '@sighthost/ui';
import { adminSettingsService } from '@/domains/settings/services/adminSettingsService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { ConfirmDialog } from '@/shared/components/feedback/ConfirmDialog';

type Staff = {
  id: string;
  email: string | null;
  role: string;
  last_sign_in_at: string | null;
};

const ROLE_TONE: Record<string, 'active' | 'info' | 'pending' | 'draft' | 'beta'> = {
  super_admin: 'active',
  admin: 'info',
  staff: 'pending',
  support: 'beta',
  customer: 'draft',
};

type AssignableRole = 'customer' | 'support' | 'staff' | 'admin' | 'super_admin';
// Roles an admin (not super_admin) may assign — mirrors the staff-role-set EF.
const ADMIN_ASSIGNABLE: AssignableRole[] = ['support', 'customer'];

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  if (ms < 30 * 86_400_000) return `${Math.round(ms / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short' });
}

export default function StaffPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const myRole = (user?.app_metadata as { role?: string } | undefined)?.role;
  const isSuperAdmin = myRole === 'super_admin';
  const isAdmin = myRole === 'admin';
  // Admins can grant support/customer; super_admins can assign any role.
  const canManageRoles = isSuperAdmin || isAdmin;

  const [rows, setRows] = useState<Staff[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoteRole, setPromoteRole] = useState<AssignableRole>(isSuperAdmin ? 'staff' : 'support');
  const [busy, setBusy] = useState(false);
  // F-27: was a window.confirm. Losing admin access is not a browser-dialog
  // decision — and this one names a real person.
  const [demoting, setDemoting] = useState<{ id: string; email: string } | null>(null);

  function reload() {
    adminSettingsService.listStaff().then(setRows).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
    });
  }

  useEffect(reload, []);

  // What roles the current user may assign to a given target row.
  function assignableRolesFor(currentRole: string): AssignableRole[] {
    if (isSuperAdmin) return ['customer', 'support', 'staff', 'admin', 'super_admin'];
    if (isAdmin) {
      // Admins may only toggle support/customer, and only on rows that are
      // themselves support/customer (never touch staff/admin/super_admin).
      return ['customer', 'support'].includes(currentRole) ? ADMIN_ASSIGNABLE : [];
    }
    return [];
  }

  async function changeRole(target: { user_id?: string; email?: string }, role: AssignableRole) {
    setBusy(true);
    try {
      const r = await adminSettingsService.setStaffRole({ ...target, role });
      toast({
        tone: 'success',
        title: 'Role updated',
        body: `${r.email}: ${r.previous_role ?? '(unknown)'} → ${r.new_role}`,
      });
      reload();
    } catch (e) {
      toast({ tone: 'danger', title: 'Failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function submitPromote() {
    if (!promoteEmail.trim()) {
      toast({ tone: 'danger', title: 'Email required' });
      return;
    }
    await changeRole({ email: promoteEmail.trim() }, promoteRole);
    setPromoteOpen(false);
    setPromoteEmail('');
    setPromoteRole(isSuperAdmin ? 'staff' : 'support');
  }

  return (
    <div className="space-y-4">
      <Card interactive={false} className="border-warning/30 bg-warning/5">
        <div className="flex items-start gap-3 p-4 text-sm">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div className="flex-1">
            <p className="font-medium text-foreground">Staff access controls portal admin functions</p>
            <p className="mt-1 text-muted-foreground">
              <strong>super_admin</strong> can change settings, edit system_setting, manage staff, see audit logs.{' '}
              <strong>admin</strong> can use most admin pages (customers, billing, support).{' '}
              <strong>staff</strong> handles support + day-to-day ops.{' '}
              <strong>support</strong> assists customers — individual client pages, per-client billing, hosting, domains, resend invoices — but not confidential financial reports.{' '}
              <strong>customer</strong> sees only the /app portal.
            </p>
            {isAdmin && (
              <p className="mt-2 text-muted-foreground">
                As an admin you can grant or remove <strong>support</strong> access. Minting staff / admin / super_admin requires a super_admin.
              </p>
            )}
            {!canManageRoles && (
              <p className="mt-2 text-warning">
                Read-only view — you don't have permission to change roles.
              </p>
            )}
          </div>
        </div>
      </Card>

      {error && <NotificationCard tone="danger" title="Could not load staff" body={error} />}

      <Card interactive={false}>
        <header className="flex items-center justify-between border-b border-border p-4">
          <h3 className="text-sm font-semibold text-foreground">Current staff</h3>
          <Button
            size="sm"
            disabled={!canManageRoles || busy}
            onClick={() => setPromoteOpen(true)}
            title={!canManageRoles ? 'Requires admin or super_admin' : undefined}
          >
            <Plus className="h-4 w-4" /> {isSuperAdmin ? 'Promote a user' : 'Grant support access'}
          </Button>
        </header>

        {!rows ? (
          <UniversalLoadingState variant="spinner" caption="Loading…" />
        ) : rows.length === 0 ? (
          <p className="p-12 text-center text-sm text-muted-foreground">
            No staff users yet. Promote a customer above to get started.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {rows.map((r) => {
              const isMe = r.id === user?.id;
              return (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 p-3 text-sm">
                  <div className="min-w-0">
                    <p className="text-foreground">
                      {r.email ?? '—'}
                      {isMe && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Last sign in: {formatRelative(r.last_sign_in_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge tone={ROLE_TONE[r.role] ?? 'draft'}>
                      <span className="inline-flex items-center gap-1">
                        <ShieldCheck className="h-3 w-3" />
                        {r.role}
                      </span>
                    </StatusBadge>
                    {(() => {
                      const options = assignableRolesFor(r.role);
                      if (isMe || options.length === 0) return null;
                      return (
                        <select
                          value={options.includes(r.role as AssignableRole) ? r.role : ''}
                          onChange={(e) => changeRole({ user_id: r.id }, e.target.value as AssignableRole)}
                          disabled={busy}
                          title={isSuperAdmin ? 'Change role' : 'Grant or remove support access'}
                          className="h-8 rounded border border-input bg-card px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
                        >
                          {/* Show current role as a disabled anchor when it's outside the assignable set (admins viewing a support row still see 'support'). */}
                          {!options.includes(r.role as AssignableRole) && (
                            <option value="" disabled>{r.role}</option>
                          )}
                          {options.map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      );
                    })()}
                    {isSuperAdmin && r.role !== 'customer' && !isMe && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        loading={busy}
                        onClick={() => setDemoting({ id: r.id, email: r.email ?? r.id })}
                      >
                        <UserMinus className="h-3.5 w-3.5" /> Demote
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Modal
        open={promoteOpen}
        onOpenChange={(v) => !v && setPromoteOpen(false)}
        title={isSuperAdmin ? 'Promote a user' : 'Grant support access'}
        description="The user must already exist as a portal customer. Find their email in /admin/customers."
        size="default"
      >
        <ModalBody>
          <div className="space-y-3">
            <FormField label="Email" htmlFor="prom-email">
              <Input
                id="prom-email"
                type="email"
                value={promoteEmail}
                onChange={(e) => setPromoteEmail(e.target.value)}
                disabled={busy}
                autoFocus
                placeholder="user@example.co.za"
              />
            </FormField>
            <FormField label="Role" htmlFor="prom-role">
              <select
                id="prom-role"
                value={promoteRole}
                onChange={(e) => setPromoteRole(e.target.value as AssignableRole)}
                disabled={busy}
                className="h-10 w-full rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <option value="support">support</option>
                {isSuperAdmin && <option value="staff">staff</option>}
                {isSuperAdmin && <option value="admin">admin</option>}
                {isSuperAdmin && <option value="super_admin">super_admin</option>}
              </select>
            </FormField>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setPromoteOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submitPromote} loading={busy} disabled={busy}>Promote</Button>
        </ModalFooter>
      </Modal>

      {/* F-27. Demotion is immediate and names a real colleague — it deserves
          more than a browser prompt. The email must be typed: this is the one
          action here where a mis-click removes someone else's access. */}
      <ConfirmDialog
        open={demoting !== null}
        onOpenChange={(v) => !v && setDemoting(null)}
        title="Remove staff access?"
        description={
          demoting
            ? `${demoting.email} becomes a normal customer immediately. Any admin, sales or support screen they have open will stop loading on the next request.`
            : undefined
        }
        confirmLabel="Demote to customer"
        tone="danger"
        confirmWord={demoting?.email}
        busy={busy}
        onConfirm={() => {
          if (demoting) {
            changeRole({ user_id: demoting.id }, 'customer');
            setDemoting(null);
          }
        }}
      />
    </div>
  );
}
